// src/pages/Admin/ModuleScoringGuide.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import TitleWithBack from '../../components/common/TitleWithBack';
import { downloadBulkUploadTemplate } from '../../utils/bulkUploadTemplates';

function ModuleScoringGuide() {
  const { id: moduleId } = useParams();
  const [moduleDetails, setModuleDetails] = useState(null);
  const [criteria, setCriteria] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [tooltip, setTooltip] = useState('');

  const fetchCriteria = () => {
    setLoading(true);
    axios.get(`/api/scoring-criteria/module/${moduleId}`)
      .then(res => setCriteria(res.data || []))
      .catch(() => setTooltip('Error fetching scoring guide.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    axios.get(`/api/modules/${moduleId}`)
      .then(res => setModuleDetails(res.data))
      .catch(() => setTooltip('Error fetching module details.'));
    fetchCriteria();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    setIsUploading(true);
    setTooltip('');
    const formData = new FormData();
    formData.append('file', uploadFile);
    try {
      await axios.post(`/api/scoring-criteria/module/${moduleId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setTooltip('Scoring guide uploaded successfully!');
      setUploadFile(null);
      fetchCriteria();
    } catch (err) {
      const backendMessage = (err.response && typeof err.response.data === 'string')
        ? err.response.data
        : 'Error uploading scoring guide.';
      setTooltip(`Error: ${backendMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  const totalMax = criteria.reduce((sum, c) => sum + (c.maxScoreProcess || 0) + (c.maxScoreProduct || 0), 0);

  return (
    <div className="container mt-2">
      <TitleWithBack title="Scoring Guide" />
      {moduleDetails && (
        <p className="text-muted">
          {moduleDetails.moduleName} ({moduleDetails.moduleCode}) &mdash; {moduleDetails.examType?.name}
        </p>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title">Upload / Replace Scoring Guide</h5>
          <p className="text-muted small">
            Re-uploading replaces the entire scoring guide for this module. The file is read the same way as
            the printed UVTAB scoring guide: everything above the first question is ignored, a "Question One",
            "Question Two", ... row opens each group, and the lettered rows beneath it (a), (b), ... are its
            criteria - Assessment Criteria, Scoring Guide, then Max Score Process and Max Score Product.
            Sub-Total and TOTAL SCORE rows are skipped automatically, and reading stops at the GRAND TOTAL
            row. You can literally fill in the printed scoring guide template with your own questions and
            upload it as-is. The final marksheet percentage is calculated against whatever the Process+Product
            max scores actually total across every row - there's no fixed target to hit. Delete the example
            question in the downloaded template before filling in your own.
          </p>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm mb-3"
            onClick={() => downloadBulkUploadTemplate('scoringGuide')}
          >
            Download Template
          </button>
          <form onSubmit={handleUpload}>
            <div className="mb-3">
              <input
                type="file"
                className="form-control"
                accept=".xls,.xlsx"
                onChange={(e) => setUploadFile(e.target.files[0])}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={!uploadFile || isUploading}>
              {isUploading ? 'Uploading...' : 'Upload Scoring Guide'}
            </button>
          </form>
          {tooltip && (
            <div className={`mt-3 alert ${tooltip.startsWith('Error') ? 'alert-danger' : 'alert-success'}`}>
              {tooltip}
            </div>
          )}
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <h5 className="card-title">Current Scoring Guide {totalMax > 0 && `(Max total: ${totalMax})`}</h5>
          {loading ? (
            <p>Loading...</p>
          ) : criteria.length === 0 ? (
            <p>Nothing to show here</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-sm">
                <thead>
                  <tr>
                    <th>Question</th>
                    <th>Part</th>
                    <th>Assessment Criteria</th>
                    <th>Scoring Guide</th>
                    <th>Max Score (Process)</th>
                    <th>Max Score (Product)</th>
                  </tr>
                </thead>
                <tbody>
                  {criteria.map(c => (
                    <tr key={c.id}>
                      <td>{c.questionLabel || `Q${c.questionNumber ?? '-'}`}</td>
                      <td>{c.partLabel}</td>
                      <td>{c.assessmentCriteria}</td>
                      <td>{c.scoringGuide}</td>
                      <td className="text-center">{c.maxScoreProcess}</td>
                      <td className="text-center">{c.maxScoreProduct}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ModuleScoringGuide;
